import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faUsers } from "@fortawesome/free-solid-svg-icons";
import "../../style/DonorInventory.css";

const RATE_COLORS = {
  great: "#669d2e",
  good: "#e0a030",
  low: "#d4574e",
};

const DonorMatchingInsights = ({ acceptanceSummary = [], darkMode }) => {
  const { t } = useTranslation();

  const receivers = useMemo(() => {
    const map = {};

    for (const row of acceptanceSummary) {
      const id = row.receiverId;
      if (!map[id]) map[id] = { id, receiver: row.receiver, totalDelivered: 0, totalAccepted: 0, materials: {} };
      map[id].totalDelivered += row.totalDelivered;
      map[id].totalAccepted += row.totalAccepted;

      const mat = row.material || "Unknown";
      if (!map[id].materials[mat]) map[id].materials[mat] = { delivered: 0, accepted: 0, reason: null };
      map[id].materials[mat].delivered += row.totalDelivered;
      map[id].materials[mat].accepted += row.totalAccepted;
      if (row.rejectionReason) map[id].materials[mat].reason = row.rejectionReason;
    }

    return Object.values(map)
      .map((r) => ({
        ...r,
        acceptanceRate: r.totalDelivered > 0 ? (r.totalAccepted / r.totalDelivered) * 100 : 100,
      }))
      .sort((a, b) => b.totalDelivered - a.totalDelivered);
  }, [acceptanceSummary]);

  if (receivers.length === 0) return null;

  const fmtLbs = (v) => v.toLocaleString(undefined, { maximumFractionDigits: 1 });

  const getRateColor = (rate) => {
    if (rate >= 90) return RATE_COLORS.great;
    if (rate >= 70) return RATE_COLORS.good;
    return RATE_COLORS.low;
  };

  const getInsight = (receiver) => {
    if (receiver.acceptanceRate >= 95) return t("Great match");
    if (receiver.acceptanceRate >= 80) return t("Good — minor mismatches");

    const worst = Object.entries(receiver.materials)
      .map(([mat, d]) => ({ mat, rate: d.delivered > 0 ? (d.accepted / d.delivered) * 100 : 100, reason: d.reason }))
      .sort((a, b) => a.rate - b.rate)[0];

    if (worst?.rate < 70 && worst.reason) {
      const reasons = { wrong_material: "wrong material", contaminated: "contamination", damaged: "damage", expired: "expiry", other: "other issues" };
      return t("{{material}}: {{rate}}% — reason: {{reason}}", {
        material: worst.mat,
        rate: worst.rate.toFixed(0),
        reason: reasons[worst.reason] || worst.reason,
      });
    }
    return t("Consider routing some materials to a different receiver");
  };

  return (
    <div className="chart-card chart-card--full">
      <div className="chart-card-header">
        <div className="chart-card-icon blue">
          <FontAwesomeIcon icon={faUsers} />
        </div>
        <h5 className="chart-card-title">{t("Matching Insights")}</h5>
      </div>

      <div className="donor-materials-table-wrapper">
        <table className="donor-materials-table">
          <thead>
            <tr>
              <th>{t("Receiver")}</th>
              <th className="number-col">{t("Delivered")}</th>
              <th className="number-col">{t("Accepted")}</th>
              <th className="number-col">{t("Rate")}</th>
              <th>{t("Insight")}</th>
            </tr>
          </thead>
          <tbody>
            {receivers.map((r) => (
              <tr key={r.id}>
                <td className="material-name-cell">
                  <div className="material-name-content">
                    <span>{r.receiver}</span>
                  </div>
                </td>
                <td className="number-col">{fmtLbs(r.totalDelivered)}</td>
                <td className="number-col">{fmtLbs(r.totalAccepted)}</td>
                <td className="number-col" style={{ color: getRateColor(r.acceptanceRate), fontWeight: 600 }}>
                  {r.acceptanceRate.toFixed(1)}%
                </td>
                <td>
                  <span className="chart-tooltip-subtitle" style={{ margin: 0 }}>{getInsight(r)}</span>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default DonorMatchingInsights;
